import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { formatCampaignMoney } from '../components/ContactAgentMetricCard.utils';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';
import type { WorkQueueItem, WorkQueueKind, WorkQueueResult } from '@workspace/types';

type KindFilter = 'all' | WorkQueueKind;

const KIND_META: Record<WorkQueueKind, { label: string; badge: 'default' | 'secondary' | 'destructive' }> = {
  PAYMENT_REVIEW: { label: 'Pago por confirmar', badge: 'default' },
  EXCEPTION: { label: 'Caso abierto', badge: 'destructive' },
  EXPIRED_PROMISE: { label: 'Compromiso vencido', badge: 'destructive' },
  WAITING_REPLY: { label: 'Esperando respuesta', badge: 'secondary' },
};

const FILTERS: Array<{ id: KindFilter; label: string }> = [
  { id: 'all', label: 'Todo' },
  { id: 'PAYMENT_REVIEW', label: 'Pagos' },
  { id: 'EXCEPTION', label: 'Casos' },
  { id: 'EXPIRED_PROMISE', label: 'Vencidos' },
  { id: 'WAITING_REPLY', label: 'Esperando' },
];

function waitingLabel(since: string): string {
  const ms = Date.now() - new Date(since).getTime();
  if (Number.isNaN(ms) || ms < 0) {
    return '';
  }
  const hours = Math.floor(ms / (60 * 60 * 1000));
  if (hours < 1) {
    return 'hace menos de 1 h';
  }
  if (hours < 48) {
    return `hace ${hours} h`;
  }
  return `hace ${Math.floor(hours / 24)} dias`;
}

function filterCount(queue: WorkQueueResult, filter: KindFilter): number {
  if (filter === 'all') {
    return queue.items.length;
  }
  return queue.counts[filter];
}

export function ContactAgentWorkQueuePage() {
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [queue, setQueue] = useState<WorkQueueResult | null>(null);
  const [filter, setFilter] = useState<KindFilter>('all');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void contactAgentApi
      .getWorkQueue()
      .then(result => {
        if (!cancelled) {
          setQueue(result);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  const items = queue
    ? queue.items.filter(item => filter === 'all' || item.kind === filter)
    : [];

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        title='Bandeja'
        description='Todo lo que espera una acción tuya, ordenado por prioridad: pagos por confirmar, casos abiertos, compromisos vencidos y clientes sin responder.'
        showBack={false}
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
      />

      {error ? <p className='text-destructive'>{error}</p> : null}

      <Card className='rounded-2xl shadow-sm'>
        <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-3'>
          <CardTitle className='text-lg'>Pendientes</CardTitle>
          <div className='flex flex-wrap items-center gap-2'>
            {FILTERS.map(option => (
              <Button
                key={option.id}
                type='button'
                size='sm'
                variant={filter === option.id ? 'secondary' : 'outline'}
                onClick={() => setFilter(option.id)}
              >
                {option.label}
                {queue ? ` (${filterCount(queue, option.id)})` : ''}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className='space-y-3'>
          {queue && items.length === 0 ? (
            <div className='rounded-xl border border-dashed px-4 py-10 text-center text-sm text-muted-foreground'>
              {filter === 'all'
                ? 'No hay nada pendiente de acción humana. El bot sigue gestionando solo.'
                : 'Nada pendiente en esta categoría.'}
            </div>
          ) : null}
          {items.map(item => (
            <WorkQueueRow key={item.id} item={item} />
          ))}
          {!queue && !error ? (
            <p className='text-sm text-muted-foreground'>Cargando bandeja…</p>
          ) : null}
        </CardContent>
      </Card>
    </ContactAgentPage>
  );
}

function WorkQueueRow({ item }: { item: WorkQueueItem }) {
  const meta = KIND_META[item.kind];
  return (
    <div className='grid gap-2 rounded-xl border px-4 py-3 md:grid-cols-[auto_1.2fr_2fr_auto_auto] md:items-center'>
      <Badge variant={meta.badge} className='justify-self-start whitespace-nowrap'>
        {meta.label}
      </Badge>
      <div>
        <Link
          to={`/admin/contact-agent/contacts/${item.contactId}`}
          className='font-medium text-primary hover:underline'
        >
          {item.contactLabel}
        </Link>
        <p className='text-xs text-muted-foreground'>{item.phoneE164}</p>
      </div>
      <p className='text-sm'>{item.reason}</p>
      <div className='text-sm md:text-right'>
        {item.amount ? <p className='font-medium'>{formatCampaignMoney(item.amount)}</p> : null}
        <p className='text-xs text-muted-foreground'>{waitingLabel(item.since)}</p>
      </div>
      <Button asChild size='sm' variant='outline'>
        <Link to={`/admin/contact-agent/contacts/${item.contactId}`}>Ver ficha</Link>
      </Button>
    </div>
  );
}
